import { api } from './api'
import type { APISession } from '@/types/session'

export interface DatasourceBillVersion {
    version: number
    md5_hash: string
    created_at: string
    text_extracted: boolean
    textract_job_id?: string
    text_s3_key?: string
}

export interface DatasourceBill {
    bill_number: string
    title: string
    synopsis?: string
    session: string
    session_code: string
    crossfile_bill_number?: string
    data_source: string
    appkey: string
    versions: DatasourceBillVersion[]
    full_text_available: boolean
    pdf_url?: string
}

export interface DatasourceBillsResponse {
    items: DatasourceBill[]
    next_token?: string
}

export interface ListDatasourceBillsParams {
    data_source: string
    session_code: APISession
    limit?: number
    next_token?: string
    start_key?: string
}

const transformDatasourceBillResponse = (data: any): DatasourceBill => {
    return {
        bill_number: data.BillNumber || data.bill_number,
        title: data.Title || data.title,
        synopsis: data.Synopsis || data.synopsis,
        session: data.Session || data.session,
        session_code: data.SessionCode || data.session_code,
        crossfile_bill_number: data.CrossfileBillNumber || data.crossfile_bill_number,
        data_source: data.DataSource || data.data_source,
        appkey: data.appkey,
        versions: (data.Versions || data.versions || []).map((v: any) => ({
            version: v.Version || v.version,
            md5_hash: v.MD5Hash || v.md5_hash,
            created_at: v.CreatedAt || v.created_at,
            text_extracted: v.TextExtracted || v.text_extracted,
            textract_job_id: v.TextractJobId || v.textract_job_id,
            text_s3_key: v.TextS3Key || v.text_s3_key,
        })),
        full_text_available: data.FullTextAvailable || data.full_text_available || false,
        pdf_url: data.PDFURL || data.pdf_url
    }
}

export const datasourceBillsApi = {
    listDatasourceBills: async ({
        data_source,
        session_code,
        limit = 100,
        next_token,
        start_key
    }: ListDatasourceBillsParams): Promise<DatasourceBillsResponse> => {
        const params = new URLSearchParams()
        
        if (limit) params.append('limit', limit.toString())
        if (next_token) params.append('next_token', next_token)
        if (start_key) params.append('start_key', start_key)

        try {
            const requestUrl = `/datasource/${encodeURIComponent(data_source)}/bills/${encodeURIComponent(session_code)}?${params.toString()}`
            console.log('[datasourceBillsApi] Making request to:', requestUrl)
            
            const response = await api.get<any>(requestUrl, {
                isGlobal: false // This is not an admin endpoint, but a direct datasource endpoint
            })
            
            console.log('[datasourceBillsApi] Response status:', response.status)
            console.log('[datasourceBillsApi] Raw response data:', response.data)

            const transformedResponse: DatasourceBillsResponse = {
                items: Array.isArray(response.data.items)
                    ? response.data.items.map(transformDatasourceBillResponse)
                    : [],
                next_token: response.data.next_token
            }

            console.log('[datasourceBillsApi] Transformed response:', transformedResponse.items.length, 'items')
            return transformedResponse
        } catch (error: any) {
            console.error('Failed to fetch datasource bills:', error.message)
            throw error
        }
    }
}
